import { useEffect } from 'react';
import { View } from '@shared/components/View';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MAX_BEFORE_TRUNCATION } from '@shared/scenes/chat/thread/components/message/constants';
import { MBSeenBySheet } from '@shared/scenes/chat/thread/components/seenBy/sheet';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { MBSeenByImages } from './images';
import { MBSeenByCountText, MBSeenByCountTextProps, MBSeenWithoutCountText } from './text';

interface MBSeenByProps {
  /*
        A list of all seen by users
    */
  users: API.ShortUserFragment[];

  /*
        Sender of message
   */
  sender?: API.ShortUserFragment;

  /*
        If set, seen by will be shown with images.
        Otherwise it will show with text.
    */
  showImages?: boolean;

  /*
        If set, seen by will show as "Seen".
        Otherwise, it will only show "Seen by X"
    */
  isSeenByCountHidden?: boolean;

  /*
        Callback when the seen by modal list is shown
    */
  onShowDetails?: () => void;

  // From MBSeenByCountTextProps
  color?: MBSeenByCountTextProps['color'];

  anchorName: string;
  align: 'right' | 'left';
}

export function MBSeenBy({
  onShowDetails,
  users,
  sender,
  showImages,
  isSeenByCountHidden,
  color,
  anchorName,
  align = 'left',
}: MBSeenByProps) {
  const [isShowingList, showList, hideList] = useBoolean(false);

  useEffect(() => {
    if (isShowingList) {
      onShowDetails?.();
    }
  }, [isShowingList, onShowDetails]);

  return (
    <>
      <View row>
        <View style={{ anchorName }}>
          <MBTouchableOpacity onPress={showList}>
            <SeenByContent
              showImages={showImages}
              isSeenByCountHidden={isSeenByCountHidden}
              color={color}
              users={users}
            />
          </MBTouchableOpacity>
        </View>
      </View>
      {isShowingList && (
        <MBSeenBySheet
          onClose={hideList}
          users={users}
          sender={sender}
          anchorName={anchorName}
          align={align}
        />
      )}
    </>
  );
}

function SeenByContent({
  showImages,
  isSeenByCountHidden,
  color,
  users,
}: Omit<MBSeenByProps, 'anchorName' | 'align'>) {
  if (showImages) {
    return (
      <MBSeenByImages
        users={users}
        isCountVisible={users.length > MAX_BEFORE_TRUNCATION && !isSeenByCountHidden}
      />
    );
  }

  if (isSeenByCountHidden) {
    return <MBSeenWithoutCountText color={color} />;
  }

  return <MBSeenByCountText count={users.length} color={color} />;
}
